import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, X, AlertCircle, Gift } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackLogin, trackSignup } from '../utils/analytics';
import { trackEvent, trackEventOnce, trackCtaClick } from '../utils/visitorTracking';
import { ensureProfile } from '../services/profileService';
import { clearStoredReferralCode, getStoredReferralCode, storeReferralCode } from '../utils/referral';
import { clearSelectedPlan, getSelectedPlan, normalizePlanParam, storeSelectedPlan } from '../utils/selectedPlan';
import { createPayment, redirectToPayment } from '../services/paymentService';
import { markWelcomeModalPending } from '../utils/upgradePrompts';
export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signInWithGoogle
  } = useAuth();
  const getInitialView = (): 'login' | 'signup' | 'forgot' => {
    if (location.pathname === '/login') return 'login';
    if (location.pathname === '/signup') return 'signup';
    return 'signup';
  };
  const [view, setView] = useState<'login' | 'signup' | 'forgot'>(getInitialView());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resetOtpVerified, setResetOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  // A referral link (/signup?ref=CODE) is captured on load and preserved
  // through signup. Reward validation happens server-side.
  useEffect(() => {
    const incoming = new URLSearchParams(location.search).get('ref');
    if (incoming) {
      storeReferralCode(incoming);
      setReferralCode(incoming);
      return;
    }
    setReferralCode(getStoredReferralCode());
  }, [location.search]);
  // A plan chosen before authenticating (/signup?plan=monthly) is preserved so
  // checkout resumes the moment the account is ready.
  useEffect(() => {
    const incomingPlan = normalizePlanParam(new URLSearchParams(location.search).get('plan'));
    if (incomingPlan) storeSelectedPlan(incomingPlan);
  }, [location.search]);
  /**
   * Continues an interrupted checkout with the plan the visitor selected
   * before authenticating. Falls back to the normal destination.
   */
  const resumeCheckoutOrNavigate = async (fallback: string) => {
    const plan = getSelectedPlan();
    if (!plan) {
      navigate(fallback);
      return;
    }
    try {
      const {
        authorization_url
      } = await createPayment(plan);
      clearSelectedPlan();
      redirectToPayment(authorization_url);
    } catch (err) {
      console.error('Resume checkout failed:', err);
      navigate(fallback);
    }
  };
  // Helper function to format error messages with troubleshooting tips
  const formatError = (err: any): string => {
    const errorMessage = err.message || err.toString();
    // Database error saving new user - trigger failure
    if (errorMessage.includes('Database error saving new user') || errorMessage.includes('database error') || errorMessage.includes('Database error')) {
      return 'Account setup error: There was a problem creating your account. Please try again. If the issue persists, try using Google Sign-In instead.';
    }
    // Network/fetch errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('fetch')) {
      return 'Connection error: Unable to reach authentication server. Please check your internet connection and try again. If using a VPN or ad blocker, try disabling it temporarily.';
    }
    // CORS errors
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      return 'Connection blocked: Your browser is blocking the authentication request. Please check your browser extensions or try a different browser.';
    }
    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      return 'Request timeout: The server took too long to respond. Please check your internet connection and try again.';
    }
    // Return original message for other errors
    return errorMessage;
  };
  useEffect(() => {
    if (location.pathname === '/login') {
      setView('login');
      resetForm();
    } else if (location.pathname === '/signup') {
      setView('signup');
      resetForm();
    }
  }, [location.pathname]);
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    return requirements;
  };
  const passwordRequirements = validatePassword(newPassword || password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setOtpSent(false);
    setResetOtpVerified(false);
    setError(null);
    setSuccess(null);
    setShowPasswordRequirements(false);
  };
  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    // Track Google OAuth initiation
    await trackCriticalEvent('Google_OAuth_Initiated', {
      auth_action: view === 'signup' ? 'signup' : 'login',
      page_location: 'auth_page'
    });
    // First-party: a Google signup begins here, at the click — not on mount.
    // Logins are excluded so the funnel only counts real signup intent.
    if (view === 'signup') {
      void trackEvent('signup_started', {
        method: 'google'
      });
    }
    try {
      await signInWithGoogle();
      // User will be redirected to Google OAuth
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(formatError(err));
      setGoogleLoading(false);
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    trackCtaClick('Create Account', 'auth_page_signup_form', '/onboarding');
    void trackEvent('signup_started', {
      method: 'email'
    });
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
            ...(referralCode ? {
              referral_code: referralCode
            } : {})
          }
        }
      });
      if (error) throw error;
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists');
        return;
      }
      // Fire-and-forget: Call Cloudflare bot protect edge function
      supabase.functions.invoke('cloudflare-bot-protect').catch(err => {
        console.error('[Bot Protection] Edge function failed silently:', err);
      });
      // Supabase sends a 6-digit OTP code to the user's email
      // Switch to the OTP input screen so the user can verify
      setOtpSent(true);
      setSuccess('Verification code sent! Please check your email and enter the 6-digit code below.');
    } catch (err: any) {
      console.error('Signup error:', err);
      console.error('Signup error code:', err?.code, '| status:', err?.status, '| message:', err?.message);
      if (err?.message?.toLowerCase().includes('unexpected') || err?.code === 'unexpected_failure') {
        console.warn('[KNOWN ISSUE] Unexpected Failure detected during signup. ' + 'The user may have been created successfully despite this error. ' + 'Check the Authentication tab in Supabase to verify. ' + 'This was previously caused by broken Welcome Email triggers (now purged).');
      }
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup'
      });
      if (error) throw error;
      if (data.session) {
        // Ensure profile exists in the profiles table (safety net for trigger failures)
        const userId = data.session.user.id;
        await ensureProfile(userId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          fullName: `${firstName.trim()} ${lastName.trim()}`,
          email: email
        });
        // Track successful signup
        const emailDomain = email.split('@')[1];
        await trackCriticalEvent('User_Signed_Up', {
          signup_method: 'email',
          first_name: firstName,
          last_name: lastName,
          email_domain: emailDomain,
          has_password_requirements_met: true
        });
        // GA4 only learns that a signup happened — no email, name or tokens.
        trackSignup('email');
        // First-party: only fires after auth + profile creation actually
        // succeeded, and only once per user id.
        void trackEventOnce(`signup_completed:${userId}`, 'signup_completed', {
          user_id: userId,
          method: 'email'
        });
        // The referral code has been handed to the backend with the signup
        clearStoredReferralCode();
        // Greet the new account once, on their first dashboard visit
        markWelcomeModalPending();
        // New users go to onboarding first — unless they came in mid-checkout
        await resumeCheckoutOrNavigate('/onboarding');
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    trackCtaClick('Login', 'auth_page_login_form', '/dashboard');
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      if (data.session) {
        // Track successful login
        await trackCriticalEvent('User_Logged_In', {
          login_method: 'email',
          is_returning_user: true
        });
        trackLogin('email');
        await resumeCheckoutOrNavigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleSendResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`
      });
      if (error) throw error;
      // Track password reset request
      const emailDomain = email.split('@')[1];
      await trackCriticalEvent('Password_Reset_Requested', {
        email_domain: emailDomain
      });
      setOtpSent(true);
      setSuccess('Reset code sent! Please check your email and enter the code below.');
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery'
      });
      if (error) throw error;
      setResetOtpVerified(true);
      setSuccess('Code verified! Now enter your new password.');
      setOtp('');
    } catch (err: any) {
      console.error('Reset OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      // Track password reset completion
      await trackCriticalEvent('Password_Reset_Completed', {
        reset_method: 'otp_verification'
      });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        setView('login');
        resetForm();
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-3496" data-mp="1t83jiwt041jb">
      <header className="bg-white border-b border-gray-200" data-id="element-3497" data-mp="1o9s8iiqyzkzi">
        <div className="max-w-md mx-auto px-5 py-4" data-id="element-3498" data-mp="1oabgb6mwoszt">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-3499" data-mp="1k99fwmh96aw9">
            <ArrowLeft size={16} className="mr-1" data-id="element-3500" data-mp="1f8lohzp43s1i" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-3501" data-mp="1acmxs5gb9xxx">
        <div className="w-full max-w-md" data-id="element-3502" data-mp="3lxdz96auamdq">
          <div className="bg-white rounded-xl shadow-sm p-8" data-id="element-3503" data-mp="3qwgu5xx3vc7z">
            <h1 className="text-2xl font-bold mb-2 text-center" data-id="element-3504" data-mp="3qytkayd0osw4">
              {view === 'signup' ? 'Create Account' : view === 'login' ? 'Welcome Back' : 'Reset Password'}
            </h1>
            <p className="text-gray-600 text-sm text-center mb-6" data-id="element-3505" data-mp="3387c9730j8m4">
              {view === 'signup' ? 'Start analyzing conversations for toxic patterns' : view === 'login' ? 'Sign in to your ToxicDetector account' : !otpSent ? 'Enter your email to receive a reset code' : !resetOtpVerified ? 'Enter the code sent to your email' : 'Create your new password'}
            </p>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2" data-id="element-3506" data-mp="2ofvr2cquewmq">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" data-id="element-3507" data-mp="3muujt4y0eapz" />
                <div className="flex-1" data-id="element-3508" data-mp="5dl1v7uuc7gu">{error}</div>
              </div>}

            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm" data-id="element-3509" data-mp="dye44hdz8ts">
                {success}
              </div>}

            {/* Google Sign-In Button - Show on login and signup (not on forgot password or OTP) */}
            {view !== 'forgot' && !otpSent && <>
                <GoogleSignInButton onClick={handleGoogleSignIn} loading={googleLoading} text={view === 'signup' ? 'Sign up with Google' : 'Sign in with Google'} data-id="element-3510" data-mp="3hwj6ulcf3k3j" />

                <div className="relative my-6" data-id="element-3511" data-mp="t58eqlv9pi04">
                  <div className="absolute inset-0 flex items-center" data-id="element-3512" data-mp="10meull3ex5fp">
                    <div className="w-full border-t border-gray-300" data-id="element-3513" data-mp="15lhs8kh2c9dn"></div>
                  </div>
                  <div className="relative flex justify-center text-sm" data-id="element-3514" data-mp="qoqpq2c3ehjt">
                    <span className="px-2 bg-white text-gray-500" data-id="element-3515" data-mp="qqle5qmkorsn">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </>}

            {view === 'signup' && !otpSent && <form onSubmit={handleSignup} className="space-y-4" data-id="element-3516" data-mp="1ob7nt9sngz6m">
                <div className="grid grid-cols-2 gap-3" data-id="element-3517" data-mp="19nmm9kbfb80t">
                  <div data-id="element-3518" data-mp="19n31zwyaw7jm">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3519" data-mp="1emfyl9ez4sze">
                      First Name
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3520" data-mp="22b8lkgdmbqhv" />
                  </div>
                  <div data-id="element-3521" data-mp="3uemqw5m5cetp">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3522" data-mp="3ue3a4s88fwh9">
                      Last Name
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3523" data-mp="pz3cgschl9m8" />
                  </div>
                </div>

                <div data-id="element-3524" data-mp="31pfimeww4av9">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3525" data-mp="31pp7u0a42dzw">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3526" data-mp="2wrw3zdwa5w2s" />
                </div>

                <div data-id="element-3527" data-mp="31bk8ru1ez0ke">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3528" data-mp="31cvcxuyruac1">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => {
                setPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3529" data-mp="3ab73jwzn184h" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3530" data-mp="3u0ozjfyuh993">
                    <p className="font-medium text-gray-700" data-id="element-3531" data-mp="1v37grm0p0mj">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3532" data-mp="6tei34k4zf5t">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3533" data-mp="blec6gdizduh">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3534" data-mp="liiyqhba9bbt" /> : <X size={16} className="mr-2" data-id="element-3535" data-mp="pmiakmlmblyu" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3536" data-mp="2rctuofgrpszz">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3537" data-mp="2ot5vxtwsnr3h" /> : <X size={16} className="mr-2" data-id="element-3538" data-mp="2k0mf8o2t8fug" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3539" data-mp="2a3htgpcvmme6">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3540" data-mp="32t6wt9d26yiy" /> : <X size={16} className="mr-2" data-id="element-3541" data-mp="2xsas4tkvv8ch" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3542" data-mp="2ttjtgi0rrin9">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3543" data-mp="1jt7ngeqq348q" /> : <X size={16} className="mr-2" data-id="element-3544" data-mp="1ot3ny407odjj" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                {/* Referral field — only shown when a code was actually
                    detected from the link or a previous visit. */}
                {referralCode && <div data-id="element-3545" data-mp="15u7u4ts9m168">
                    <label htmlFor="referral-code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3546" data-mp="15uho0sgfksh7">
                      Referral code
                    </label>
                    <input id="referral-code" type="text" value={referralCode} onChange={e => {
                const next = e.target.value.trim().toUpperCase();
                setReferralCode(next);
                if (next) storeReferralCode(next);
              }} autoComplete="off" spellCheck={false} aria-describedby="referral-code-status" className="w-full px-4 py-2 border border-green-300 bg-green-50 rounded-lg font-mono tracking-widest uppercase text-gray-900 focus:ring-2 focus:ring-purple-600 focus:border-transparent" data-id="element-3547" data-mp="w1k0g7wl0dbq" />
                    <div id="referral-code-status" className="mt-2 flex items-start gap-2 text-sm" data-id="element-3548" data-mp="hgelw9yxkzdg">
                      <Gift size={16} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-3549" data-mp="r6hdc5qem59o" />
                      <div data-id="element-3550" data-mp="1087cjxqj28i0">
                        <p className="font-medium text-green-700" data-id="element-3551" data-mp="1090aqabf7poj">
                          🎁 Referral code applied
                        </p>
                        <p className="mt-0.5 text-gray-600" data-id="element-3552" data-mp="1obdr2b6h5xca">
                          You can use this referral code during signup.
                        </p>
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3553" data-mp="1xzvp4knddqc8">
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3554" data-mp="1fbqmjgig8bf0">
                  Already have an account?{' '}
                  <Link to="/login" className="text-purple-600 font-medium hover:underline" data-id="element-3555" data-mp="2lpzrypsqhm26">
                    Sign in
                  </Link>
                </p>

                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-3556" data-mp="f4li0n00usva">
                  By signing up, you agree to our{' '}
                  <a href="/terms" className="text-purple-600 hover:underline" data-id="element-3557" data-mp="5cft5m52grkb">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-purple-600 hover:underline" data-id="element-3558" data-mp="bjpn9omn7ff">
                    Privacy Policy
                  </a>
                </p>
              </form>}

            {view === 'signup' && otpSent && <form onSubmit={handleVerifyOtp} className="space-y-4" data-id="element-3559" data-mp="3ilbwlq9o6e8l">
                <div data-id="element-3560" data-mp="3dlfyu8rkfj1h">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3561" data-mp="3dm90wwfkxkda">
                    Verification Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3562" data-mp="1bsjhk472bk8x" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3563" data-mp="3ozhoq04oydrv">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3564" data-mp="24jny99pd70i6">
                  Back to signup
                </button>
              </form>}

            {view === 'login' && <form onSubmit={handleLogin} className="space-y-4" data-id="element-3565" data-mp="2axjzzuvfiqv8">
                <div data-id="element-3566" data-mp="2624ld0plkqxr">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3567" data-mp="25yqk5vso6nrw">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3568" data-mp="1dce4wl0htcyh" />
                </div>

                <div data-id="element-3569" data-mp="zin01pxpih93">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3570" data-mp="ulwh8uuip7jz">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3571" data-mp="93mwspc7sb3u" />
                </div>

                <div className="flex items-center justify-between text-sm" data-id="element-3572" data-mp="s1z7ifirxt1j">
                  <button type="button" onClick={() => setView('forgot')} className="text-purple-600 hover:underline" data-id="element-3573" data-mp="x0ae8hdrvnfj">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3574" data-mp="16rwpueu0voit">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3575" data-mp="2v86ch99y58jz">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-purple-600 font-medium hover:underline" data-id="element-3576" data-mp="2r8e878rl5okf">
                    Sign up
                  </Link>
                </p>
              </form>}

            {view === 'forgot' && !otpSent && <form onSubmit={handleSendResetOtp} className="space-y-4" data-id="element-3577" data-mp="3eztetdqeas1h">
                <div data-id="element-3578" data-mp="3a5fjfbbqgkwt">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3579" data-mp="3a4w1vvpzvyjb">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3580" data-mp="6if79j8ykfrb" />
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3581" data-mp="3lr4d6sfxt3bx">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>

                <button type="button" onClick={() => {
              setView('login');
              resetForm();
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3582" data-mp="8hzkdwy0yivk">
                  Back to login
                </button>
              </form>}

            {view === 'forgot' && otpSent && !resetOtpVerified && <form onSubmit={handleVerifyResetOtp} className="space-y-4" data-id="element-3583" data-mp="1ausly1ptgnqu">
                <div data-id="element-3584" data-mp="1asojzpbh9yt7">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3585" data-mp="1evulkjnnb1tc">
                    Reset Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3586" data-mp="10vjw9l1zhvaa" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3587" data-mp="n45chc095q26">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3588" data-mp="3lg9xtd95xxqx">
                  Back to email entry
                </button>
              </form>}

            {view === 'forgot' && resetOtpVerified && <form onSubmit={handleResetPassword} className="space-y-4" data-id="element-3589" data-mp="3210bo9iqte3">
                <div data-id="element-3590" data-mp="3u9dju9xvjix6">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3591" data-mp="3u9wowq2fects">
                    New Password
                  </label>
                  <input type="password" value={newPassword} onChange={e => {
                setNewPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3592" data-mp="2nucitptzsajq" />
                </div>

                <div data-id="element-3593" data-mp="1d2ejfn8hm9n4">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3594" data-mp="1d2wn34xhbq52">
                    Confirm New Password
                  </label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3595" data-mp="1m0gtesa78cmj" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3596" data-mp="1nran97jqd89">
                    <p className="font-medium text-gray-700" data-id="element-3597" data-mp="1s17dr4bvgvph">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3598" data-mp="1n61pvzywt6yr">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3599" data-mp="212favj2njqhz">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3600" data-mp="19a6cg9na4o2r" /> : <X size={16} className="mr-2" data-id="element-3601" data-mp="14b39cuqv0rxf" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3602" data-mp="1ddmddi6dtal9">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3603" data-mp="lklunu2v3wv2" /> : <X size={16} className="mr-2" data-id="element-3604" data-mp="bhggmuz9pdfq" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3605" data-mp="1ld897dtoikc">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3606" data-mp="ua0v37e7zg00" /> : <X size={16} className="mr-2" data-id="element-3607" data-mp="z7kndb7r5tay" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3608" data-mp="p9wg5lsymtbw">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3609" data-mp="3a1yhmpad9dnz" /> : <X size={16} className="mr-2" data-id="element-3610" data-mp="1gh50oqzy016w" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3611" data-mp="16ps79uyhx2yr">
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}